import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../../lib/api.js';
import type { SaveState } from '../../components/ui/index.js';
import type { Settings } from '../../lib/settings-types.js';
import type { Me } from '../../lib/types.js';

export function useSettings() {
  return useQuery({ queryKey: ['settings'], queryFn: () => api.get<Settings>('/api/settings') });
}

/** Whether the signed-in user is an admin (token/invite minting, shared
 * secrets). Cached under the same key AccountPane uses. */
export function useIsAdmin(): boolean {
  const me = useQuery({ queryKey: ['me'], queryFn: () => api.get<Me>('/api/auth/me') });
  return me.data?.isAdmin ?? false;
}

export const ADMIN_NOTE = 'Managed by an admin — ask one if something here needs changing.';

/** POST helper that refreshes the settings read afterwards. */
export function useSettingsPost<TBody = unknown, TRes = { ok: boolean }>(url: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: TBody) => api.post<TRes>(url, body),
    onSettled: () => void qc.invalidateQueries({ queryKey: ['settings'] }),
  });
}

export function fmtWhen(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

export const HOURS = Array.from({ length: 24 }, (_, h) => h);

/** Resolve a save with this to mean "the user backed out", not "it worked". */
export const SAVE_CANCELLED = Symbol('save-cancelled');

/** The human half of an error: ApiError carries the server's own message. */
export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  return err instanceof Error ? err.message : 'something went wrong';
}

/**
 * A control that saves the moment you change it, and says so.
 *
 * The value shown is the server's, except while a change is in flight or has
 * failed — then it is yours. That is the point: before this, a rejected change
 * was reverted by the next settings refetch with no message at all, so the
 * control appeared to undo itself and the reason was never shown. Here the
 * failed value stays put, the reason sits next to it, and the server's value is
 * only adopted again once it actually agrees.
 */
export function useInstantField<T>(
  serverValue: T,
  save: (value: T) => Promise<unknown>,
): { value: T; set: (value: T) => void; state: SaveState } {
  const [pending, setPending] = useState<{ value: T } | null>(null);
  const [state, setState] = useState<SaveState>({ status: 'idle' });

  // Let go of the local value only when the server comes back agreeing with it.
  useEffect(() => {
    if (pending && Object.is(pending.value, serverValue)) setPending(null);
  }, [pending, serverValue]);

  // A "Saved" tick is a confirmation, not a permanent label.
  useEffect(() => {
    if (state.status !== 'saved') return;
    const t = setTimeout(() => setState({ status: 'idle' }), 2500);
    return () => clearTimeout(t);
  }, [state]);

  const set = (value: T): void => {
    setPending({ value });
    setState({ status: 'saving' });
    save(value).then(
      (result) => {
        // A save can end in a third way: the user was asked a follow-up
        // question and backed out. That is not a success and not a failure —
        // drop the local value and say nothing.
        if (result === SAVE_CANCELLED) {
          setPending(null);
          setState({ status: 'idle' });
        } else {
          setState({ status: 'saved' });
        }
      },
      (err: unknown) => setState({ status: 'error', message: errorMessage(err) }),
    );
  };

  return { value: pending ? pending.value : serverValue, set, state };
}
